import { flowRight, flow, map } from 'lodash/fp';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { formValues } from 'redux-form';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import { countries, fiscalParticularCases, nationalities } from 'common/choices';
import Icon from 'common/components/Icon';
import getFiscalParticularCases from 'common/utils/getFiscalParticularCases';
import sortedList from 'common/utils/sortedList';
import addPrefix from '../../../utils/addPrefix';
import { DateField, SelectField, TextField, PhoneInputField, MultiSelectField, YesNoField } from '../../form/field';
import FieldInfo from '../../form/FieldInfo';
import { GridLayout } from '../../form/layout';
import CityField from './CityField';
import BirthCityField from './BirthCityField';
import isRequiredGrandChildrenAndPartnerField from '../../../common/utils/isRequiredGrandChildrenAndPartnerField';
import SearchAdress from './SearchAddress';
import formatNameAndLastName from '../../../utils/formatLastNameAndFirstName';
import { useSession } from '../../sessions';
import './CivilStatus.scss';
import { setModal } from '../../../actions';
import ModalUpdateEmailShoyo from './ModalUpdateEmailShoyo';
import withRouter from '../../withRouter';
import EditableEmailField from './EditableEmailField';

const CivilStatus = ({
    birthCountry,
    civility,
    partner,
    required,
    partnerBirthCountry,
    partnerCivility,
    familySituation,
    country,
    createdAt,
    onClickEmailInput,
    disabledPersonalEmail,
    disabledPartnerPersonalEmail,
    birthDate,
    partnerBirthDate,
}) => {
    const computeAge = (birthDateTarget) => {
        if (!birthDateTarget) { return null; }
        const birth = new Date(birthDateTarget);
        const today = new Date();

        if (birth > today) { return null; }
        let age = today.getFullYear() - birth.getFullYear();
        const month = today.getMonth() - birth.getMonth();

        if (month < 0 || (0 === month && today.getDate() < birth.getDate())) {
            age--;
        }

        return age;
    };
    const age = computeAge(partner ? partnerBirthDate : birthDate);
    const session = useSession();
    const { instanceTheseis } = session;

    const getIdentifier = addPrefix('partner')(partner);

    const isRequiredField = isRequiredGrandChildrenAndPartnerField(createdAt);

    const estUneFemmeMarieOuVeuve = (situation) => {
        if ('single' === situation || 'cohabitation' === situation || 'divorced' === situation || 'partner' === situation) {
            return false;
        }

        return (!partner && 'Mme' === civility) || (partner && 'Mme' === partnerCivility);
    };

    const sortedNationalities = sortedList(nationalities);

    // Note: empty divs allow to fill a block in the grid
    return (
        <Fragment>
            <h5 className="title">
                <Icon icon="arrow-right" />
                {partner ? 'Informations personnelles de votre conjoint' : 'Vos informations personnelles'}
            </h5>
            <GridLayout className="triple condensed gray-box full-width">
                {(!partner && 'Mme' === civility) || (partner && 'Mme' === partnerCivility) ? (
                    <Fragment>
                        <div className="middle">
                            <SelectField name={getIdentifier('civility')} title="Civilité" required>
                                <option />
                                <option>M</option>
                                <option>Mme</option>
                            </SelectField>
                            <TextField
                                title={`Prénom${partner ? ' du conjoint' : ''}`}
                                placeholder="Saisir le prénom"
                                required={partner ? isRequiredField : required}
                                name={getIdentifier('firstName')}
                                format={(value) => formatNameAndLastName(value)}
                            />
                        </div>
                        <div className="middle">
                            <TextField
                                title={`Nom${partner ? ' du conjoint' : ''}`}
                                placeholder="Saisir le nom"
                                required={partner ? isRequiredField : required}
                                name={getIdentifier('lastName')}
                                format={(value) => formatNameAndLastName(value, true)}
                            />
                            <TextField
                                title={`Nom de naissance ${partner ? ' du conjoint' : ''}`}
                                placeholder="Saisir le nom"
                                name={getIdentifier('birthName')}
                                required={estUneFemmeMarieOuVeuve(familySituation) ? required : null}
                            />

                        </div>
                    </Fragment>
                ) : (
                    <Fragment>
                        <SelectField name={getIdentifier('civility')} title="Civilité" required>
                            <option />
                            <option>M</option>
                            <option>Mme</option>
                        </SelectField>
                        <TextField
                            title={`Prénom${partner ? ' du conjoint' : ''}`}
                            placeholder="Saisir le prénom"
                            required={partner ? isRequiredField : required}
                            name={getIdentifier('firstName')}
                            format={(value) => formatNameAndLastName(value)}
                        />
                        <TextField
                            title={`Nom${partner ? ' du conjoint' : ''}`}
                            placeholder="Saisir le nom"
                            required={partner ? isRequiredField : required}
                            name={getIdentifier('lastName')}
                            format={(value) => formatNameAndLastName(value, true)}
                        />
                    </Fragment>
                )}
                {!partner ? (
                    <Fragment>
                        <div className="double">
                            <SearchAdress />
                        </div>
                        <TextField title="Numéro" placeholder="10, 10 bis" name="addressNumber" />
                        <TextField title="Type de voie" placeholder="Rue, Chemin, Allée" name="addressType" required={'FR' === country && required} />
                        <TextField title="Libellé voie" placeholder="du Moulin" required={required} name="address" />
                        <TextField title="Complément d'adresse" placeholder="Batiment, Résidence, Appartement" name="addressComplement" />
                        {
                            'FR' === country ?
                                <Fragment>
                                    <SelectField
                                        title="Pays"
                                        name={getIdentifier('country')}
                                    >
                                        <option />
                                        {Object.entries(countries).map(([key, value]) => (
                                            <option key={key} value={key}>
                                                {value}
                                            </option>
                                        ))}
                                    </SelectField>
                                    <CityField required />
                                </Fragment> :
                                <Fragment>
                                    <SelectField
                                        title="Pays"
                                        name={getIdentifier('country')}
                                    >
                                        <option />
                                        {Object.entries(countries).map(([key, value]) => (
                                            <option key={key} value={key}>
                                                {value}
                                            </option>
                                        ))}
                                    </SelectField>
                                    <TextField title="Code Postal" name="postalCode" />
                                    <TextField title="Ville" name="city" />
                                </Fragment>
                        }
                    </Fragment>
                ) : null}
                <PhoneInputField
                    title={`Téléphone mobile${partner ? ' du conjoint' : ''}`}
                    placeholder="33612345678"
                    required={required || !partner}
                    name={getIdentifier('mobile')}
                />
                {!partner ? <PhoneInputField title="Téléphone fixe" placeholder="33123456789" name="phone" /> : null}

                <EditableEmailField
                    isPartner={!!partner}
                    required={required}
                    instanceTheseis={instanceTheseis}
                    disabledPersonalEmail={disabledPersonalEmail}
                    disabledPartnerPersonalEmail={disabledPartnerPersonalEmail}
                    onClickEmailInput={onClickEmailInput}
                />
                <TextField
                    title={`Email professionnel${partner ? ' du conjoint' : ''}`}
                    placeholder="adresse@email.com"
                    name={getIdentifier('professionalEmail')}
                />
                <div className="container-birthdate">
                    <DateField
                        title={`Date de naissance${partner ? ' du conjoint' : ''}`}
                        required={required}
                        name={getIdentifier('birthDate')}
                    />
                    <div className="age-field">
                        <div className="form-group-grid">
                            <h6> {`Âge ${partner ? ' du conjoint' : ''}`}</h6>
                            <input
                                className="filled"
                                value={age !== null ? age : ''}
                                readOnly
                            />
                        </div>
                    </div>
                </div>
                <SelectField
                    title={`Pays de naissance${partner ? ' du conjoint' : ''}`}
                    name={getIdentifier('birthCountry')}
                >
                    <option />
                    {Object.entries(countries).map(([key, value]) => (
                        <option key={key} value={key}>
                            {value}
                        </option>
                    ))}
                </SelectField>
                {(!partner && 'FR' === birthCountry) || (partner && 'FR' === partnerBirthCountry) ? (
                    <Fragment>
                        <div className="double">
                            <BirthCityField partner={partner} />
                        </div>
                    </Fragment>
                ) : null
                }
                <SelectField
                    title="Résidence fiscale"
                    name={getIdentifier('taxResidency')}
                >
                    <option />
                    {Object.entries(countries).map(([key, value]) => (
                        <option key={key} value={key}>
                            {value}
                        </option>
                    ))}
                </SelectField>
                <YesNoField
                    title={(
                        <Fragment>
                            US PERSON
                            <FieldInfo id="remainingIncomesPopover">
                                <div>
                                    <p>
                                        est considérée comme US PERSON un(e) citoyen(e) américain(e) ou un(e)
                                        résident(e) américain(e) ou une personne née aux Etats-Unis
                                    </p>
                                </div>
                            </FieldInfo>
                        </Fragment>
                    )}
                    name={getIdentifier('usPerson')}
                />
                <SelectField
                    title={`Nationalité${partner ? ' du conjoint' : ''}`}
                    name={getIdentifier('nationality')}
                    required={partner ? isRequiredField : required}
                >
                    <option />
                    {Object.entries(sortedNationalities).map(([key, value]) => (
                        <option key={key} value={key}>{value}</option>
                    ))}
                </SelectField>
                <MultiSelectField
                    name={getIdentifier('fiscalParticularCases')}
                    title="Cas fiscaux particuliers"
                    double
                    options={flow([
                        Object.entries,
                        map(([partCase, settings]) => ({ value: partCase, label: settings.title })),
                    ])(getFiscalParticularCases(familySituation, fiscalParticularCases))}
                />
            </GridLayout>
        </Fragment>
    );
};

CivilStatus.propTypes = {
    partner: PropTypes.bool,
    required: PropTypes.bool,
    birthCountry: PropTypes.string,
    partnerBirthCountry: PropTypes.string,
    civility: PropTypes.string,
    partnerCivility: PropTypes.string,
    familySituation: PropTypes.string,
    country: PropTypes.string,
    createdAt: PropTypes.string,
    onClickEmailInput: PropTypes.func,
    disabledPartnerPersonalEmail: PropTypes.bool,
    disabledPersonalEmail: PropTypes.bool,
    initialValues: PropTypes.shape({
        idBoxShoyo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        partnerIdBoxShoyo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        personalEmail: PropTypes.string,
        partnerPersonalEmail: PropTypes.string,
    }),
    birthDate: PropTypes.string,
    partnerBirthDate: PropTypes.string,
};

CivilStatus.defaultProps = {
    partner: false,
};

const mapStateToProps = (state, { params: { id } }) => {
    const {
        id: ripId,
        personalEmail,
        partnerPersonalEmail,
        idBoxShoyo,
        partnerIdBoxShoyo,
        firstName,
        lastName,
        partnerFirstName,
        partnerLastName,
        mobile,
        phone,
        partnerBirthDate,
        birthDate,
        ripPdfBase,
    } = state.data.rip[id] || {};
    const disabledPersonalEmail = idBoxShoyo && personalEmail !== null;
    const disabledPartnerPersonalEmail = partnerIdBoxShoyo && partnerPersonalEmail !== null;

    return {
        ripId,
        personalEmail,
        partnerPersonalEmail,
        idBoxShoyo,
        partnerIdBoxShoyo,
        firstName,
        lastName,
        partnerFirstName,
        partnerLastName,
        mobile,
        phone,
        partnerBirthDate,
        birthDate,
        ripPdfBase,
        disabledPersonalEmail,
        disabledPartnerPersonalEmail,
    };
};

export default compose(flowRight([
    formValues({
        civility: 'civility',
        partnerCivility: 'partnerCivility',
        birthCountry: 'birthCountry',
        partnerBirthCountry: 'partnerBirthCountry',
        birthZipcode: 'birthZipcode',
        partnerBirthZipcode: 'partnerBirthZipcode',
        required: 'dualRip',
        familySituation: 'familySituation',
        country: 'country',
        createdAt: 'createdAt',
        seachAdress: 'seachAdress',
        birthDate: 'birthDate',
        partnerBirthDate: 'partnerBirthDate',
    }),
    withProps(({ required = false, cohabitation = false }) => ({
        required: cohabitation ? required : true,
    })),
    withRouter,
    connect(mapStateToProps),
    connect(
        null,
        (dispatch, ownProps) => ({
            onClickEmailInput: (e, isPartner = false) => {
                dispatch(setModal(() => (
                    <ModalUpdateEmailShoyo
                        initialValues={{
                            email: isPartner ? ownProps.partnerPersonalEmail : ownProps.personalEmail,
                            id: ownProps.ripId,
                            idBoxShoyo: ownProps.idBoxShoyo,
                            partnerIdBoxShoyo: ownProps.partnerIdBoxShoyo,
                            firstName: ownProps.firstName,
                            lastName: ownProps.lastName,
                            partnerFirstName: ownProps.partnerFirstName,
                            partnerLastName: ownProps.partnerLastName,
                            isPartner,
                        }}
                        onCancel={() => dispatch(setModal(null))}
                    />
                )));
            },
        }),
    ),
]))(CivilStatus);
